import { useState, useMemo, useEffect } from "react";
import {
    Drawer,
    Form,
    Input,
    Select,
    InputNumber,
    Space,
    Divider,
    Spin
} from "antd";
import ImageWithSpinner from "../content/imagewithspinner";
import { ForcedUpsellDetailDto, TriggerConditionDto } from "../../types/upselltypes";
import { renderBadgeSwatch, getBadgeColorName } from "./upsellbadgeutils";
import {
    lookupMenuItems,
    getMenuItemDetail
} from "../../services/menuservice";
import { MenuItemLookup } from "../../types/menutypes";
import CancelButton from "../common/cancelbutton";
import FormButton from "../common/formbutton";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import { ImageFallback } from "../../data/constants";
import debounce from "lodash/debounce";

const { Option } = Select;

interface Props {
    open: boolean;
    editing: ForcedUpsellDetailDto | null;
    form: any;
    onClose: () => void;
    onSave: () => Promise<void>;
    storeGuid: string;
    loadingForm: boolean;
}

const ForcedUpsellDrawer: React.FC<Props> = ({
    open,
    editing,
    form,
    onClose,
    onSave,
    storeGuid,
    loadingForm
}) => {

    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState<MenuItemLookup[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);

    const imageUrl = Form.useWatch("imageUrl", form);
    const source = Form.useWatch("source", form);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchItems = async (query = "") => {
        if (!source) return;

        setLoadingItems(true);
        try {
            const data = await lookupMenuItems(source, query, 20);
            setItems(data.items);
        } finally {
            setLoadingItems(false);
        }
    };

    useEffect(() => {
        if (!source) {
            setItems([]);
            form.resetFields([
                "menuGuid",
                "displayName",
                "imageUrl",
                "price",
                "ageRestricted"
            ]);
            return;
        }

        fetchItems("");
    }, [source]);

    const debouncedSearch = useMemo(
        () => debounce((value: string) => fetchItems(value), 300),
        [source]
    );

    useEffect(() => {
        const hydrateSelectedItem = async () => {
            if (!editing?.menuGuid) return;

            setItems(prev => {
                const exists = prev.some(i => i.menuGuid === editing.menuGuid);
                if (exists) return prev;

                return prev;
            });
            if (editing.displayName) {
                setItems(prev => [
                    {
                        id: editing.itemId,
                        menuGuid: editing.menuGuid,
                        name: editing.displayName ?? editing.itemId ?? "Unknown Item"
                    },
                    ...prev
                ]);
                return;
            }

            try {
                const data = await getMenuItemDetail(editing.menuGuid, storeGuid);

                setItems(prev => {
                    const exists = prev.some(i => i.menuGuid === editing.menuGuid);
                    if (exists) return prev;

                    return [
                        {
                            id: data.menuGuid,
                            menuGuid: data.menuGuid,
                            name: data.name
                        },
                        ...prev
                    ];
                });

            } catch {
                setItems(prev => [
                    {
                        id: editing.itemId,
                        menuGuid: editing.menuGuid,
                        name: editing.itemId || "Unknown Item"
                    },
                    ...prev
                ]);
            }
        };

        hydrateSelectedItem();

    }, [editing, storeGuid]);

    useEffect(() => {
        if (!open) {
            setItems([]);
            setLoadingDetail(false);
            setLoadingItems(false);
        }
    }, [open]);


    const handleSelectItem = async (menuGuid: string) => {
        if (!storeGuid) return;

        setLoadingDetail(true);

        try {
            const data = await getMenuItemDetail(menuGuid, storeGuid);

            form.setFieldsValue({
                menuGuid: data.menuGuid,
                displayName: data.name,
                imageUrl: data.imageUrl,
                price: data.price,
                ageRestricted: data.ageRestricted
            });
        } finally {
            setLoadingDetail(false);
        }
    };

    const footer = (
        <div style={{ textAlign: "right" }}>
            <Space>
                <CancelButton onClick={onClose} disabled={saving} />

                <FormButton
                    title={editing ? "Save" : "Add"}
                    icon={editing ? <EditOutlined /> : <PlusOutlined />}
                    loading={saving}
                    onClick={async () => {
                        try {
                            await form.validateFields();
                            setSaving(true);
                            await onSave();
                        } catch (err: any) {
                            if (err?.errorFields?.length) {
                                form.scrollToField(err.errorFields[0].name, {
                                    behavior: "smooth",
                                    block: "center"
                                });
                            }
                        } finally {
                            setSaving(false);
                        }
                    }}
                />
            </Space>
        </div>
    );

    return (
        <Drawer
            title={editing ? "Edit Forced Upsell" : "Add Forced Upsell"}
            open={open}
            onClose={onClose}
            width={420}
            destroyOnClose
            footer={footer}
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px',
                },
            }}
        >
            <Spin spinning={loadingForm} tip="Loading upsell..." size="default">
                <Form layout="vertical" form={form} preserve={false}>

                    <Divider>Item</Divider>

                    <Form.Item
                        name="source"
                        label="Source"
                        rules={[{ required: true }]}
                    >
                        <Select placeholder="Select source">
                            <Option value="menu">Menu</Option>
                            <Option value="product">Product</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="menuGuid"
                        label="Item"
                        rules={[{ required: true, message: "Item is required" }]}
                    >
                        <Select
                            showSearch
                            placeholder="Search and select item"
                            disabled={!source}
                            loading={loadingItems}
                            filterOption={false}
                            onSearch={debouncedSearch}
                            onDropdownVisibleChange={(open) => {
                                if (open && items.length === 0 && source) {
                                    fetchItems("");
                                }
                            }}
                            onChange={handleSelectItem}
                        >
                            {items.map(item => (
                                <Option key={item.menuGuid} value={item.menuGuid}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Spin spinning={loadingDetail} tip="Loading item details...">

                        <Divider style={{ margin: "8px 0" }} />

                        <Form.Item name="displayName" label="Display Name">
                            <Input />
                        </Form.Item>

                        <Form.Item name="imageUrl" label="Image URL">
                            <Input />
                        </Form.Item>

                        {imageUrl && (
                            <div style={{ width: "150px", height: "150px", margin: "0 auto", marginBottom: 16 }}>
                                <ImageWithSpinner imageUrl={imageUrl} />
                            </div>
                        )}

                        <Form.Item name="price" label="Price">
                            <InputNumber style={{ width: "100%" }} />
                        </Form.Item>

                        <Form.Item name="ageRestricted" label="Age Restriction">
                            <Input />
                        </Form.Item>

                    </Spin>

                    <Divider>Rules</Divider>

                    <Form.Item name="badgeText" label="Badge Text">
                        <Input maxLength={24} showCount />
                    </Form.Item>

                    <Form.Item name="badgeStyle" label="Badge Style">
                        <Select allowClear>
                            <Option value="savings">{renderBadgeSwatch("savings")} {getBadgeColorName("savings")}</Option>
                            <Option value="bundle">{renderBadgeSwatch("bundle")} {getBadgeColorName("bundle")}</Option>
                            <Option value="loyalty">{renderBadgeSwatch("loyalty")} {getBadgeColorName("loyalty")}</Option>
                            <Option value="withloyaltycardonly">{renderBadgeSwatch("withloyaltycardonly")} {getBadgeColorName("withloyaltycardonly")}</Option>
                            <Option value="new">{renderBadgeSwatch("new")} {getBadgeColorName("new")}</Option>
                            <Option value="popular">{renderBadgeSwatch("popular")} {getBadgeColorName("popular")}</Option>
                            <Option value="limited">{renderBadgeSwatch("limited")} {getBadgeColorName("limited")}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="dayParts" label="Day Parts">
                        <Select mode="multiple" allowClear>
                            <Option value="morning">Morning</Option>
                            <Option value="midday">Midday</Option>
                            <Option value="evening">Evening</Option>
                            <Option value="night">Night</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="reason" label="Reason">
                        <Input />
                    </Form.Item>

                </Form>
            </Spin>
        </Drawer>
    );
};

export default ForcedUpsellDrawer;